/* global URL */
// Shared, dependency-free contract for packaging, publishing and the marketing client.
export const LINK_CHANNELS = {
  dev: {
    appId: 'com.paddiq.link.dev',
    productName: 'Paddiq Link Dev',
    packageName: 'paddiq-link-dev',
  },
  // Permanent production distribution track, including stable versions. Keep its identity/feed.
  beta: { appId: 'com.paddiq.link', productName: 'Paddiq Link', packageName: 'paddiq-link' },
};
export const LINK_TARGETS = {
  'mac-arm64': { platform: 'mac', arch: 'arm64', extension: 'dmg', label: 'macOS · Apple silicon' },
  'mac-x64': { platform: 'mac', arch: 'x64', extension: 'dmg', label: 'macOS · Intel' },
  'win-x64': { platform: 'win', arch: 'x64', extension: 'exe', label: 'Windows · x64' },
};
export function validVersion(channel, version) {
  if (typeof version !== 'string' || version.length > 256) return false;
  const number = '(0|[1-9]\\d*)';
  const core = `${number}\\.${number}\\.${number}`;
  const pattern =
    channel === 'dev'
      ? `${core}-dev\\.${number}\\.${number}`
      : channel === 'beta'
        ? `${core}(-beta\\.${number})?`
        : null;
  // Reject noncanonical/oversized numbers before handing a version to the native SemVer updater.
  return (
    pattern !== null &&
    new RegExp(`^${pattern}$`).exec(version)?.[0] === version &&
    version
      .split(/[.-]/)
      .filter((part) => /^\d+$/.test(part))
      .every((part) => Number.isSafeInteger(Number(part)))
  );
}
export function releaseStage(channel, version) {
  if (!validVersion(channel, version)) return null;
  return channel === 'dev' ? 'dev' : version.includes('-') ? 'beta' : 'stable';
}
export function installerName(channel, version, target) {
  if (!LINK_CHANNELS[channel] || !validVersion(channel, version) || !LINK_TARGETS[target]) {
    throw new Error('Invalid Link channel, version or target');
  }
  return `Paddiq-Link-${channel}-${version}-${target}.${LINK_TARGETS[target].extension}`;
}
export function releaseRequest({ event, ref, runNumber, runAttempt, baseVersion, betaVersion }) {
  if (!['push', 'workflow_dispatch'].includes(event)) throw new Error('Unsupported release event');
  if (ref === 'refs/heads/dev') {
    if (
      releaseStage('beta', baseVersion) !== 'stable' ||
      !/^[1-9]\d*$/.test(runNumber) ||
      !/^[1-9]\d*$/.test(runAttempt)
    ) {
      throw new Error('Invalid development build version');
    }
    const version = `${baseVersion}-dev.${runNumber}.${runAttempt}`;
    if (!validVersion('dev', version)) throw new Error('Invalid development build version');
    return { channel: 'dev', version };
  }
  if (
    event === 'workflow_dispatch' &&
    ref === 'refs/heads/main' &&
    validVersion('beta', betaVersion)
  )
    return { channel: 'beta', version: betaVersion };
  throw new Error('Use dev or dispatch an explicit production version from protected main');
}
export function channelBaseUrl(bucket, channel) {
  if (!/^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/.test(bucket) || !LINK_CHANNELS[channel])
    throw new Error('Invalid dedicated download bucket or channel');
  return `https://storage.googleapis.com/${bucket}/paddiq-link/${channel}/`;
}
export function validateManifest(value, channel, manifestUrl) {
  if (
    !value ||
    value.schemaVersion !== 1 ||
    value.channel !== channel ||
    !validVersion(channel, value.version) ||
    !/^[a-f0-9]{40}$/.test(value.commit) ||
    !Number.isFinite(Date.parse(value.publishedAt)) ||
    !Array.isArray(value.downloads) ||
    value.downloads.length < 1 ||
    value.downloads.length > 3
  )
    throw new Error('Invalid Link release manifest');
  const base = new URL('.', manifestUrl);
  if (
    base.protocol !== 'https:' ||
    base.username ||
    base.password ||
    !base.pathname.endsWith(`/paddiq-link/${channel}/`)
  )
    throw new Error('Invalid channel URL');
  const seen = new Set();
  for (const download of value.downloads) {
    const filename = installerName(channel, value.version, download.target);
    const expected = new URL(`${value.version}/${filename}`, base).href;
    if (
      seen.has(download.target) ||
      download.url !== expected ||
      !Number.isSafeInteger(download.size) ||
      download.size <= 0 ||
      !/^[a-f0-9]{64}$/.test(download.sha256) ||
      !(
        download.signed === true ||
        (['dev', 'beta'].includes(channel) &&
          download.target === 'win-x64' &&
          download.signed === false &&
          download.verification === `unsigned-${channel}`)
      ) ||
      (download.signed === true &&
        download.verification != null &&
        download.verification !== 'signed')
    )
      throw new Error('Invalid Link installer');
    seen.add(download.target);
  }
  return value;
}

export function compareVersions(channel, left, right) {
  if (!validVersion(channel, left) || !validVersion(channel, right))
    throw new Error('Invalid version comparison');
  const parts = (version) => version.replace(`-${channel}.`, '.').split('.').map(BigInt);
  const a = parts(left);
  const b = parts(right);
  for (let index = 0; index < 3; index += 1) {
    if (a[index] !== b[index]) return a[index] > b[index] ? 1 : -1;
  }
  // A final version follows every prerelease of the same core version.
  if (a.length !== b.length) return a.length === 3 ? 1 : -1;
  for (let index = 3; index < a.length; index += 1) {
    if (a[index] !== b[index]) return a[index] > b[index] ? 1 : -1;
  }
  return 0;
}

// Shared by installed clients and pointer promotion: stable never returns to a preview,
// even a higher-numbered one. Skipping any number of intermediate releases is supported.
export function isForwardVersion(channel, candidate, current) {
  return (
    compareVersions(channel, candidate, current) > 0 &&
    !(releaseStage(channel, current) === 'stable' && releaseStage(channel, candidate) !== 'stable')
  );
}

export function pinnedReleaseRequest(options, pinned) {
  const current = releaseRequest(options);
  if (!pinned?.channel && !pinned?.version) return current;
  if (pinned.channel !== current.channel || !validVersion(pinned.channel, pinned.version))
    throw new Error('Invalid preflight release identity');
  if (current.channel === 'beta') {
    if (pinned.version !== current.version) throw new Error('Preflight beta version changed');
  } else {
    const prefix = `${options.baseVersion}-dev.${options.runNumber}.`;
    if (!pinned.version.startsWith(prefix)) throw new Error('Preflight build version changed');
    const attempt = BigInt(pinned.version.slice(prefix.length));
    if (attempt < 1n || attempt > BigInt(options.runAttempt))
      throw new Error('Invalid preflight attempt');
  }
  return { channel: pinned.channel, version: pinned.version };
}
